import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { format } from 'date-fns';
import { Subscription } from 'rxjs';
import {
  createEmploymentAvailability,
  deleteEmploymentAvailability,
  updateEmploymentAvailability,
} from 'server/shared/helpers/employmentAvailabilityHelper';
import { randomId } from 'server/shared/helpers/random';
import { trimPhone } from 'server/shared/helpers/trimPhoneHelper';
import { CarLicenseEnum } from 'server/shared/models/carLicense.enum';
import { Certificate } from 'server/shared/models/certificate';
import {
  EmploymentExternalTransferInfo,
  EmploymentStatusEnum,
} from 'server/shared/models/employment';
import {
  EmploymentInvitee,
  ExternalEmploymentInviteeInput,
} from 'server/shared/models/employmentInvitee';
import { EmploymentInviteeStatusEnum } from 'server/shared/models/employmentInviteeStatusEnum';
import { InviteeFileReference } from 'server/shared/models/invitee';
import { InviteeFile } from 'server/shared/models/inviteeFile';
import { LanguageEnum } from 'server/shared/models/language.enum';
import { SexEnum } from 'server/shared/models/sex.enum';
import { Speciality } from 'server/shared/models/speciality';
import {
  EmploymentAvailability,
  EmploymentAvailabilityDay,
  EmploymentAvailabilityStatusEnum,
  UnavailabilityReasonEnum,
} from 'server/shared/models/user';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { getEmploymentInviteeStatusAndIcon } from 'src/app/shared/getInviteeStatusAndIcon';
import { downloadFile } from '../../../helpers/downloadFile';
import {
  getAvailabilityMatchPercentage,
  getAvailabilityMatchPercentageRange,
} from '../../../helpers/employment/getAvailabilityMatchPercentage';
import { ToastService } from '../../services/toast/toast.service';
import { TranslationService } from '../../services/translation.service';
import { ConfirmationDialog } from '../../shared/confirmation-dialog/confirmation.component';
import {
  EmploymentAvailabilityEditDialogComponent,
  EmploymentAvailabilityEditDialogData,
} from '../../temp-work/availability-edit/availability-edit-dialog/availability-edit-dialog.component';

@Component({
  selector: 'add-employment-invitee-form',
  templateUrl: 'add-employment-invitee.component.html',
  styleUrls: ['add-employment-invitee.component.scss'],
})
export class AddEmploymentInviteeComponent implements OnInit {
  i18n = this.translationService.getTranslations('employments');
  employmentId?: string;
  secret?: string;
  statusEnum = EmploymentStatusEnum;
  inviteeStatusEnum = EmploymentInviteeStatusEnum;
  availabilityStatusEnum = EmploymentAvailabilityStatusEnum;
  isLoadingJob = true;

  sexEnum = SexEnum;
  languageEnum = LanguageEnum;
  carLicenseEnum = CarLicenseEnum;
  note: any;

  subscriptions: Subscription = new Subscription();

  employmentExternalTransferInfo?: EmploymentExternalTransferInfo;
  form: FormGroup = new FormGroup({});
  showForm = false;
  canNotDeclineJob = false;

  availabilities: EmploymentAvailability[] = [];
  availabilityDays: EmploymentAvailabilityDay[] = [];
  loadingAvailabilities = false;

  fromDate?: Date;
  toDate?: Date;
  matchPercentage: number = 0;
  workload: number = 100;

  files: InviteeFile[] = [];
  filesForInvitee: InviteeFileReference[] = [];
  isUploading: boolean = false;
  isDownloading: boolean = false;
  progressbarValue: number = 0;

  dateOfBirth?: string;

  @ViewChild('autosize') autosize?: CdkTextareaAutosize;

  constructor(
    private router: Router,
    private toastService: ToastService,
    private ngZone: NgZone,
    private route: ActivatedRoute,
    private translationService: TranslationService,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.employmentId = this.route.snapshot.paramMap.get('employmentId') || '';
    this.secret = this.route.snapshot.paramMap.get('secret') || '';

    this.getEmploymentTransfer();

    this.form = new FormGroup(
      {
        sex: new FormControl(null, [Validators.required]),
        firstName: new FormControl(null, [Validators.required]),
        lastName: new FormControl(null, [Validators.required]),
        email: new FormControl(null, [Validators.required, Validators.email]),
        phone: new FormControl(null, [Validators.required]),
        certificates: new FormControl(null, []),
        specialities: new FormControl(null, []),
      },
      { updateOn: 'change' },
    );
  }

  getEmploymentTransfer() {
    Meteor.call(
      'getEmploymentExternalTransferInfo',
      this.employmentId,
      this.secret,
      (error: Meteor.Error, result: EmploymentExternalTransferInfo) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: 'jobNotFound',
          });
        } else {
          this.ngZone.run(() => {
            this.employmentExternalTransferInfo = result;
            this.fromDate = new Date(result.startDate);
            this.toDate = new Date(result.endDate);
            this.workload = result.workload;

            this.canNotDeclineJob =
              this.employmentExternalTransferInfo?.invitees.some(
                (invitee) =>
                  invitee.status === EmploymentInviteeStatusEnum.BOOKED,
              ) ||
              this.employmentExternalTransferInfo?.declined ||
              false;
            this.files = [];
            this.filesForInvitee = [];
          });
        }
        this.ngZone.run(() => {
          this.isLoadingJob = false;
        });
      },
    );
  }

  getInviteeIcon(invitee: EmploymentInvitee) {
    return getEmploymentInviteeStatusAndIcon(invitee).stateIcon;
  }

  getIconClass(invitee: EmploymentInvitee) {
    if (invitee.status === EmploymentInviteeStatusEnum.BOOKED) {
      return 'book';
    } else if (
      invitee.status === EmploymentInviteeStatusEnum.CANCELLED ||
      invitee.status === EmploymentInviteeStatusEnum.UNAVAILABLE ||
      invitee.status === EmploymentInviteeStatusEnum.DECLINED
    ) {
      return 'cancel';
    } else {
      return 'available';
    }
  }

  declineTransfer() {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: 'declineTransfer',
        text: this.i18n?.confirmTransferReject || 'Einsatz wirklich ablehnen?',
      },
    });
    dialogRef.afterClosed().subscribe((res: any) => {
      if (res) {
        Meteor.call(
          'declineEmploymentTransfer',
          this.employmentId,
          this.secret,
          (error: Meteor.Error) => {
            if (error) {
              this.toastService.add({
                type: 'ERROR',
                message: error.error?.toString(),
              });
              return;
            }
            this.toastService.add({
              type: 'SUCCESS',
              message: 'transferDeclined',
            });
            this.canNotDeclineJob = true;
            if (!this.employmentExternalTransferInfo) return;
            this.employmentExternalTransferInfo.declined = true;
          },
        );
      }
    });
  }

  submit() {
    if (
      this.form?.invalid ||
      !this.availabilities.length ||
      (this.employmentExternalTransferInfo?.enableFiles &&
        !this.files.length) ||
      (this.employmentExternalTransferInfo?.requiredFields?.certificates &&
        !this.form?.controls?.certificates?.value?.length) ||
      (this.employmentExternalTransferInfo?.requiredFields?.sex &&
        (this.form?.controls?.sex?.value === undefined ||
          this.form?.controls?.sex?.value === null)) ||
      (this.employmentExternalTransferInfo?.requiredFields?.dateOfBirth &&
        !this.dateOfBirth)
    ) {
      this.form.markAllAsTouched();
      return;
    }
    let invitee: ExternalEmploymentInviteeInput = {
      sex: this.form?.controls.sex.value,
      email: this.form?.controls.email.value,
      firstName: this.form?.controls.firstName.value,
      lastName: this.form?.controls.lastName.value,
      phone: this.form?.controls.phone.value,
      certificates: this.form?.controls.certificates.value,
      specialities: this.form?.controls.specialities.value,
      availabilities: this.availabilities,
      availabilityDays: this.availabilityDays,
      inviteeFiles: this.filesForInvitee,
      dateOfBirth: this.dateOfBirth,
    };

    Meteor.call(
      'addInviteeToEmployment',
      this.employmentId,
      invitee,
      this.secret,
      (error: Meteor.Error) => {
        this.loadingAvailabilities = true;
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
          return;
        }
        this.getEmploymentTransfer();
        this.toastService.add({
          type: 'SUCCESS',
          message: 'successfullyAddedInvitee',
        });
        this.form?.reset();
        this.availabilities = [];
        this.matchPercentage = 0;
        setTimeout(() => {
          this.loadingAvailabilities = false;
        });
        if (!this.form) return;
        Object.keys(this.form.controls).forEach((key) => {
          this.form?.controls[key].setErrors(null);
        });
      },
    );
  }

  trimPhone() {
    if (this.form?.controls.phone?.value) {
      this.form.controls.phone.setValue(
        trimPhone(this.form.controls.phone.value),
      );
    }
  }

  certificateSelectionChanged(certificates: Certificate[]) {
    const inviteeCertificates = certificates.map((certificate) => {
      return { id: certificate._id, displayName: certificate.name };
    });
    this.form?.controls.certificates.setValue(inviteeCertificates);
  }

  specialitySelectionChanged(specialities: Speciality[]) {
    const inviteeSpecialities = specialities.map((speciality) => {
      return { id: speciality._id, displayName: speciality.name };
    });
    this.form?.controls.specialities.setValue(inviteeSpecialities);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  openAvailabilityDialog(availability?: EmploymentAvailability): void {
    let availabilityData: EmploymentAvailabilityEditDialogData | undefined;
    const personName =
      (this.form?.controls.firstName.value || '') +
      ' ' +
      (this.form?.controls.lastName.value || '');
    if (availability) {
      availabilityData = {
        personName,
        availabilityId: availability.id,
        startDate: availability.startDate,
        endDate: availability.endDate,
        status: availability.status,
        workload: availability.workload,
        unavailableReason: availability.unavailableReason,
        notes: availability.notes,
      };
    }
    const dialogRef = this.dialog.open(
      EmploymentAvailabilityEditDialogComponent,
      {
        data: availabilityData
          ? availabilityData
          : {
              personName,
            },
      },
    );

    dialogRef
      .afterClosed()
      .subscribe(
        async (result: {
          id?: string;
          startDate: string;
          endDate: string;
          status: EmploymentAvailabilityStatusEnum;
          workload: number;
          unavailableReason?: UnavailabilityReasonEnum;
          notes?: string;
          toDelete?: boolean;
        }) => {
          if (result) {
            this.loadingAvailabilities = true;
            if (result.id) {
              if (result.toDelete) {
                const { employmentAvailabilities, employmentAvailabilityDays } =
                  deleteEmploymentAvailability(
                    result.id,
                    this.availabilities || [],
                    this.availabilityDays || [],
                  );
                this.availabilities = employmentAvailabilities;
                this.availabilityDays = employmentAvailabilityDays;
              } else {
                const { employmentAvailabilities, employmentAvailabilityDays } =
                  updateEmploymentAvailability(
                    result.id,
                    result as EmploymentAvailability,
                    this.availabilities || [],
                    this.availabilityDays || [],
                  );
                this.availabilities = employmentAvailabilities;
                this.availabilityDays = employmentAvailabilityDays;
              }
            } else {
              const availability = {
                id: randomId(),
                startDate: result.startDate,
                endDate: result.endDate,
                status: result.status,
                workload: result.workload,
                unavailableReason: result.unavailableReason,
                notes: result.notes,
              };
              const { employmentAvailabilities, employmentAvailabilityDays } =
                createEmploymentAvailability(
                  availability,
                  this.availabilities || [],
                  this.availabilityDays || [],
                );
              this.availabilities = employmentAvailabilities;
              this.availabilityDays = employmentAvailabilityDays;
            }
            this.matchPercentage = getAvailabilityMatchPercentage(
              this.availabilityDays || [],
              getAvailabilityMatchPercentageRange(
                this.employmentExternalTransferInfo?.startDate || '',
                this.employmentExternalTransferInfo?.endDate || '',
              ),
              this.workload || 100,
              this.employmentId || '',
            );
            setTimeout(() => {
              this.loadingAvailabilities = false;
            });
          }
        },
      );
  }
  removeAvailability(availability: EmploymentAvailability) {
    this.loadingAvailabilities = true;
    const { employmentAvailabilities, employmentAvailabilityDays } =
      deleteEmploymentAvailability(
        availability.id,
        this.availabilities || [],
        this.availabilityDays || [],
      );
    this.availabilities = employmentAvailabilities;
    this.availabilityDays = employmentAvailabilityDays;
    this.matchPercentage = getAvailabilityMatchPercentage(
      this.availabilityDays,
      getAvailabilityMatchPercentageRange(
        this.employmentExternalTransferInfo?.startDate || '',
        this.employmentExternalTransferInfo?.endDate || '',
      ),
      this.workload || 100,
      this.employmentId || '',
    );
    setTimeout(() => {
      this.loadingAvailabilities = false;
    });
  }

  uploadFile(event: any) {
    this.isUploading = true;
    const files = (event.target as HTMLInputElement).files;
    // 15MB
    const maxFileSize = 1024 * 1024 * 15;
    if (!FileReader || !files || !files.length) {
      this.toastService.add({ type: 'ERROR', message: 'notSupported' });
      return;
    } else {
      Array.from(files).forEach((uploadFile) => {
        const file = uploadFile as File;
        if (
          file.size < maxFileSize &&
          this.employmentExternalTransferInfo?.ownTransfer
        ) {
          const fileReader = new FileReader();
          fileReader.onload = () => {
            if (
              !fileReader.result ||
              !this.employmentExternalTransferInfo?.ownTransfer
            ) {
              this.isUploading = false;
              this.toastService.add({
                type: 'ERROR',
                message: 'fileUploadFailed',
              });
              return;
            }

            const fileId = randomId();

            const inviteeFile: InviteeFile = {
              _id: fileId,
              customerId:
                this.employmentExternalTransferInfo.ownTransfer.targetCustomer
                  .id,
              fileName: file.name,
              createdAt: Date.now(),
              updatedAt: Date.now(),
              path: fileId,
              size: file.size.toString(),
              type: file.type,
              blobString: fileReader.result?.toString(),
            };

            Meteor.call(
              'upsertInviteeFile',
              inviteeFile,
              (error: Meteor.Error, result: InviteeFile) => {
                if (error) {
                  this.isUploading = false;
                  this.toastService.add({
                    type: 'ERROR',
                    message: 'serverError',
                  });
                }
                if (result) {
                  this.isUploading = false;

                  result.type = result.fileName.split('.').pop() || '';
                  this.files.push(result);
                  this.filesForInvitee.push({
                    id: result._id,
                    fileName: result.fileName,
                    path: result.path,
                    size: result.size,
                    type: result.type,
                  });
                  this.toastService.add({
                    type: 'SUCCESS',
                    message: 'documentUploaded',
                  });
                }
              },
            );
          };
          fileReader.readAsDataURL(file);
        } else {
          this.isUploading = false;
          this.toastService.add({
            type: 'ERROR',
            message: 'maxFileUploadExceeded',
          });
        }
      });
    }
  }
  removeFileDialog(file: InviteeFile, templateRef: any) {
    const dialogRef = this.dialog.open(templateRef);
    dialogRef.afterClosed().subscribe((response) => {
      if (response) {
        this.removeFile(file);
      }
    });
  }

  removeFile(inviteeFile: InviteeFile) {
    Meteor.call(
      'removeInviteeFile',
      inviteeFile,
      (error: Meteor.Error, result: InviteeFile) => {
        if (error) {
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        if (result) {
          this.filesForInvitee = this.filesForInvitee.filter((file) => {
            return file.id !== result._id;
          });
          this.files = this.files.filter((file) => {
            return file._id !== result._id;
          });
          this.toastService.add({
            type: 'SUCCESS',
            message: 'removedSuccessfully',
          });
        }
      },
    );
  }

  downloadInviteeFile(event: Event, file: InviteeFile) {
    this.isDownloading = true;
    downloadFile(file, '', (percentage: number) => {
      this.progressbarValue = percentage;
    })
      .then(() => {
        this.isDownloading = false;
      })
      .catch(() => {
        this.toastService.add({
          type: 'ERROR',
          message: 'fileNotFound',
        });
        this.isDownloading = false;
      });
  }

  selectDateOfBirth(event: MatDatepickerInputEvent<any & string>) {
    this.dateOfBirth = format(event.value, dbDayDateToken);
  }

  clearDateOfBirth(): void {
    this.dateOfBirth = undefined;
  }
}
